'use client';
/**
 * Web プッシュ通知の購読と FCM トークンの取得・削除
 */

import * as firebase from 'firebase/app';
import { deleteToken, getMessaging, getToken } from 'firebase/messaging';

import { PushAvailability, resolvePushAvailability } from './pushDeviceState';

export type FirebaseConfigAndVapidPublicKey = {
  firebaseConfig: object;
  vapidPublicKey: string;
};

const SERVICE_WORKER_PATH = '/service-worker.js';

/**
 * Check if the browser supports service workers and push notifications
 */
export function isPushNotificationSupported(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** この端末・ブラウザでプッシュ通知を設定できるか。できないなら、その理由 */
export function getPushAvailability(): PushAvailability {
  return resolvePushAvailability({
    isSupported: isPushNotificationSupported(),
    userAgent: navigator.userAgent,
    maxTouchPoints: navigator.maxTouchPoints,
    isStandalone:
      window.matchMedia('(display-mode: standalone)').matches ||
      ('standalone' in navigator && navigator.standalone === true),
  });
}

/**
 * Register the service worker
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!isPushNotificationSupported()) {
    return null;
  }

  try {
    return await navigator.serviceWorker.register(SERVICE_WORKER_PATH);
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    return null;
  }
}

/** service worker に購読があるか */
export async function hasPushSubscription(): Promise<boolean> {
  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  return !!subscription;
}

// Firebase App は１回のみ初期化する
let firebaseInitialized = false;

/**
 * FirebaseApp を返す。最初の１回のみ初期化する。
 */
function firebaseAppSingleton(firebaseConfig: object): firebase.FirebaseApp {
  if (firebaseInitialized) {
    return firebase.getApp();
  }

  firebaseInitialized = true;
  return firebase.initializeApp(firebaseConfig);
}

let pushLock: Promise<unknown> = Promise.resolve();

/**
 * 購読とトークンを触る処理（ON・OFF・送り直し）を 1 つずつ順に動かす。
 * 送り直しの途中で OFF にされて購読が作り直される、といった食い違いを防ぐ
 */
export function withPushLock<T>(fn: () => Promise<T>): Promise<T> {
  const result = pushLock.then(fn, fn);
  pushLock = result.catch(() => undefined);
  return result;
}

const getServiceWorkerRegistration =
  async (): Promise<ServiceWorkerRegistration> => {
    const registration =
      (await navigator.serviceWorker.getRegistration()) ??
      (await registerServiceWorker());
    if (!registration) {
      throw new Error('Service Worker registration failed');
    }
    // 登録した直後は service worker がまだ有効になっておらず、購読を作れない
    await waitForActivation(registration);
    return registration;
  };

const ACTIVATION_TIMEOUT_MS = 10_000;

/** service worker が有効になるのを待つ。待ち続けて ON・OFF の順番待ちが止まらないよう、時間で打ち切る */
const waitForActivation = (registration: ServiceWorkerRegistration) =>
  new Promise<void>((resolve, reject) => {
    if (registration.active) {
      resolve();
      return;
    }
    const worker = registration.installing ?? registration.waiting;
    if (!worker) {
      reject(new Error('No service worker to activate'));
      return;
    }
    const timer = setTimeout(() => {
      worker.removeEventListener('statechange', handleStateChange);
      reject(new Error('Service Worker activation timed out'));
    }, ACTIVATION_TIMEOUT_MS);
    const handleStateChange = () => {
      if (worker.state === 'activated') {
        clearTimeout(timer);
        worker.removeEventListener('statechange', handleStateChange);
        resolve();
      } else if (worker.state === 'redundant') {
        clearTimeout(timer);
        worker.removeEventListener('statechange', handleStateChange);
        reject(new Error('Service Worker became redundant'));
      }
    };
    worker.addEventListener('statechange', handleStateChange);
  });

/**
 * FCM のトークンを取得する。購読が無ければ作る。通知の許可を得てから呼ぶこと
 * （許可を求めるダイアログは、タップへの直接の応答で出す必要があるため、ここでは求めない）
 */
export async function getFcmToken({
  firebaseConfig,
  vapidPublicKey,
}: FirebaseConfigAndVapidPublicKey): Promise<string> {
  const messaging = getMessaging(firebaseAppSingleton(firebaseConfig));
  return getToken(messaging, {
    vapidKey: vapidPublicKey,
    serviceWorkerRegistration: await getServiceWorkerRegistration(),
  });
}

/**
 * この端末の FCM トークンを FCM から消し、購読を解除する。この端末のトークンを返す
 * （購読が無いとき、トークンを取れずに購読だけ解除したときは null）
 */
export async function deleteFcmTokenOnDevice(
  config: FirebaseConfigAndVapidPublicKey,
): Promise<string | null> {
  const registration = await navigator.serviceWorker.getRegistration();
  if (!(await registration?.pushManager.getSubscription())) {
    return null;
  }

  let token: string | null = null;
  try {
    // deleteToken は service worker の登録を getToken で渡しておかないと、既定の
    // firebase-messaging-sw.js を登録しようとするので、先に getToken を呼ぶ
    token = await getFcmToken(config);
    await deleteToken(
      getMessaging(firebaseAppSingleton(config.firebaseConfig)),
    );
    return token;
  } catch (error) {
    // FCM に届かなくても、購読さえ解除すればこの端末には届かない
    console.warn('Failed to delete FCM token, unsubscribing directly', error);
    const subscription = await registration?.pushManager.getSubscription();
    await subscription?.unsubscribe();
    return token;
  }
}
